import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { debounce } from '../utils/debounce';
import { Predio, PrediosResponse } from '../types/predio';

interface UseInfiniteSearchGridProps {
  fetchFunction: (page: number, pageSize: number, search: string) => Promise<PrediosResponse>;
  initialPageSize?: number;
  searchPageSize?: number;
  searchDebounceMs?: number;
}

export const useInfiniteSearchGrid = ({
  fetchFunction,
  initialPageSize = 20,
  searchPageSize = 100,
  searchDebounceMs = 300
}: UseInfiniteSearchGridProps) => {
  const [allItems, setAllItems] = useState<Predio[]>([]);
  const [searchResults, setSearchResults] = useState<Predio[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchHasMore, setSearchHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchPage, setSearchPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [totalPredios, setTotalPredios] = useState<number>(0);
  
  const abortController = useRef<AbortController | null>(null);

  // Carga inicial sin búsqueda
  const loadInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchFunction(1, initialPageSize, '');
      setAllItems(data.data || []);
      setHasMore((data.data || []).length === initialPageSize);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error loading initial data:', error);
      setAllItems([]);
    } finally {
      setLoading(false);
    }
  }, [fetchFunction, initialPageSize]);

  // Cargar más datos sin búsqueda
  const loadMoreData = useCallback(async () => {
    if (loadingMore || !hasMore || isSearchMode) return;
    
    setLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const data = await fetchFunction(nextPage, initialPageSize, '');
      const newItems = data.data || [];
      
      setAllItems(prev => [...prev, ...newItems]);
      setCurrentPage(nextPage);
      setHasMore(newItems.length === initialPageSize);
    } catch (error) {
      console.error('Error loading more data:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [fetchFunction, initialPageSize, currentPage, hasMore, loadingMore, isSearchMode]);

  // Búsqueda inicial
  const performInitialSearch = useCallback(async (term: string) => {
    if (!term.trim()) {
      setSearchResults([]);
      setIsSearchMode(false);
      setSearchHasMore(true);
      setSearchPage(1);
      return;
    }

    // Cancelar búsqueda anterior
    if (abortController.current) {
      abortController.current.abort();
    }
    abortController.current = new AbortController();

    setSearchLoading(true);
    setIsSearchMode(true);
    try {
      // Para búsqueda, usar un pageSize más grande para obtener más resultados
      const data = await fetchFunction(1, 1000, term);
      const results = data.data || [];
      
      setSearchResults(results);
      setSearchPage(1);
      // Para búsqueda, no hay más páginas ya que cargamos todos los resultados
      setSearchHasMore(false);
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Error searching:', error);
      }
    } finally {
      setSearchLoading(false);
    }
  }, [fetchFunction]);

  // Cargar más resultados de búsqueda (deshabilitado ya que cargamos todos)
  const loadMoreSearchResults = useCallback(async () => {
    // No hacer nada, ya cargamos todos los resultados de búsqueda
    return;
  }, []);

  // Debounced search
  const debouncedSearch = useMemo(
    () => debounce(performInitialSearch, searchDebounceMs),
    [performInitialSearch, searchDebounceMs]
  );

  // Manejador de búsqueda
  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
    debouncedSearch(term);
  }, [debouncedSearch]);

  // Limpiar búsqueda
  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setSearchResults([]);
    setIsSearchMode(false);
    setSearchHasMore(true);
    setSearchPage(1);
  }, []);

  // Items visibles (búsqueda o datos normales)
  const visibleItems = useMemo(() => {
    return isSearchMode ? searchResults : allItems;
  }, [isSearchMode, searchResults, allItems]);

  // Función para cargar más (solo datos normales, búsqueda carga todo)
  const loadMore = useCallback(() => {
    if (!isSearchMode) {
      loadMoreData();
    }
  }, [isSearchMode, loadMoreData]);

  // Estados derivados
  const currentHasMore = isSearchMode ? false : hasMore; // Búsqueda no tiene más páginas
  const isLoadingAny = loading || searchLoading;

  // Efecto inicial
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (abortController.current) {
        abortController.current.abort();
      }
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return {
    visibleItems,
    loading: isLoadingAny,
    loadingMore,
    hasMore: currentHasMore,
    isSearchMode,
    searchTerm,
    totalFiltered: visibleItems.length,
    totalPredios,
    hasActiveFilters: !!searchTerm,
    loadMore,
    handleSearch,
    clearSearch,
    refreshData: loadInitialData
  };
};